import {
  IssueLiabilityCostType,
  IssueLiabilityResponsibleType,
  IssueLiabilityStatus,
  issueLiabilityCostTypeMap,
  issueLiabilityResponsibleTypeMap,
  issueLiabilityStatusMap,
} from '@gooduncles/gu-app-schema';
import { Dayjs } from 'dayjs';

/**
 * 손실 책임 폼 값 타입
 */
export interface LiabilityFormValues {
  responsibleType: IssueLiabilityResponsibleType;
  responsibleId: string | null;
  supplyAmount: number;
  taxAmount: number;
  memo: string | null;
  progressNote: string | null;
  status: IssueLiabilityStatus;
  costType: IssueLiabilityCostType;
}

/**
 * 히스토리 편집 폼 값
 */
export interface HistoryFormValues {
  createdAt: Dayjs;
  memo: string | null;
}

/**
 * 금액 입력 모드
 */
export type AmountInputMode = 'detail' | 'total';

/**
 * 책임자 유형 옵션
 */
export const responsibleTypeOptions = Object.entries(issueLiabilityResponsibleTypeMap).map(([value, label]) => ({
  value: value as IssueLiabilityResponsibleType,
  label,
}));

/**
 * 상태 옵션
 */
export const statusOptions = Object.entries(issueLiabilityStatusMap).map(([value, label]) => ({
  value: value as IssueLiabilityStatus,
  label,
}));

/**
 * 비용 유형 옵션
 */
export const costTypeOptions = Object.entries(issueLiabilityCostTypeMap).map(([value, label]) => ({
  value: value as IssueLiabilityCostType,
  label,
}));

/**
 * 책임자 유형별 Tag 색상 매핑
 */
export const liabilityResponsibleTypeColorMap: Record<IssueLiabilityResponsibleType, string> = {
  operator: 'blue',
  partners: 'purple',
  store: 'orange',
  productSupplier: 'green',
  etc: 'default',
};

/**
 * 상태별 Tag 색상 매핑
 */
export const liabilityStatusColorMap: Record<IssueLiabilityStatus, string> = {
  registered: 'blue',
  delivered: 'cyan',
  settled: 'green',
  unsettled: 'red',
  closed: 'default',
};

/**
 * 비용 유형별 Tag 색상 매핑
 */
export const liabilityCostTypeColorMap: Record<IssueLiabilityCostType, string> = {
  general: 'default',
  incidental: 'purple',
};

/**
 * 금액 포맷팅
 */
export const formatAmount = (amount: number): string => {
  return new Intl.NumberFormat('ko-KR', {
    style: 'currency',
    currency: 'KRW',
  }).format(amount);
};

/**
 * InputNumber 공통 props (천단위 콤마)
 */
export const numberInputProps = {
  formatter: (value?: number | string) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ','),
  parser: (value?: string) => value?.replace(/,/g, '') as unknown as number,
};

/**
 * 커스텀 라벨 스타일 (Form.Item 라벨과 동일하게)
 */
export const labelStyle: React.CSSProperties = {
  fontSize: 14,
  color: 'rgba(0, 0, 0, 0.88)',
  marginBottom: 8,
};

/**
 * Form.Item 공통 스타일
 */
export const formItemStyle: React.CSSProperties = { marginBottom: 0 };

/**
 * 라벨 높이에 맞춘 marginTop (Form.Item label + marginBottom = 22px)
 */
export const labelAlignStyle: React.CSSProperties = { marginTop: 22 };

/**
 * Select 공통 props
 */
export const selectCommonProps = {
  showSearch: true,
  allowClear: true,
  style: { width: 200 },
  filterOption: (input: string, option?: { label?: string }) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase()),
};
